import { Icon, Typography } from "@mg/dali/src";
import { getDeterministicAvatarColors } from "@mg/schemas/src/commons/avatarColors";
import * as Avatar from "@radix-ui/react-avatar";
import cx from "classnames";
import { forwardRef, memo, useCallback } from "react";

import { assetForUser } from "../utils/imageHandler";

interface AvatarWithInitialsProps extends React.ComponentPropsWithoutRef<
  typeof Avatar.Root
> {
  size?: number;
  square?: boolean;
  avatar?: string;
  name?: string;
  className?: string;
  hasWorkedWith?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  /**
   * Persisted placeholder background color (hex). When omitted, a STABLE
   * deterministic default is derived from `colorKey` (falling back to `name`).
   */
  backgroundColor?: string;
  /** Persisted placeholder text color (hex). Same fallback rules as above. */
  textColor?: string;
  /**
   * Stable identity key (e.g. user id / email) used to derive the deterministic
   * default colors. Prefer this over relying on `name`, which can vary per pane
   * (full name vs. display name) and changes as the user types in the editor —
   * that variation is exactly what made the old color "keep changing".
   */
  colorKey?: string;
}

export const AvatarWithInitials = memo(
  forwardRef<HTMLElement, AvatarWithInitialsProps>(
    (
      {
        size = 11,
        square,
        avatar,
        name,
        className,
        hasWorkedWith = false,
        isLoading,
        onClick,
        backgroundColor,
        textColor,
        colorKey,
        ...props
      },
      ref,
    ) => {
      const realName = name ?? "";
      const firstInitial = realName[0];
      const lastInitial =
        realName.lastIndexOf(" ") !== -1 &&
        realName[realName.lastIndexOf(" ") + 1];

      // Resolve the avatar colors: explicit persisted/picked colors win;
      // otherwise fall back to a deterministic default keyed on a stable
      // identity (colorKey) or, lastly, the name. Applied inline so the same
      // user renders identically everywhere and matches the dialog preview.
      const defaultColors = getDeterministicAvatarColors(colorKey || realName);
      const resolvedBackgroundColor =
        backgroundColor ?? defaultColors.backgroundColor;
      const resolvedTextColor = textColor ?? defaultColors.textColor;

      const containerClasses = cx(
        "aspect-square select-none align-middle items-center justify-center relative",
        { "inline-flex": !/(^|\s)(flex|block)\b/.test(className ?? "") },
        { "border-2 border-ochre-400": hasWorkedWith },
        { rounded: square },
        { "rounded-full": !square },
        className,
      );

      const getTextSize = useCallback(() => {
        // size is in 4 times px same as tailwind
        if (size > 31) {
          return "4xl";
        }
        if (size > 23) {
          return "3xl";
        }
        if (size > 15) {
          return "2xl";
        }
        if (size > 10) {
          return "lg";
        }
        if (size > 5) {
          return "sm";
        }
        return "xs";
      }, [size]);

      const avatarComponent = (
        <Avatar.Root
          className={containerClasses}
          style={{
            height: size * 4,
            backgroundColor: resolvedBackgroundColor,
            color: resolvedTextColor,
          }}
          data-testid="person-avatar"
          ref={ref}
          {...props}
        >
          {isLoading ? (
            <div className="size-8 animate-spin rounded-full border-4 border-malachite-600 border-t-transparent" />
          ) : (
            <>
              <Avatar.Image
                className={`size-full rounded-[inherit] object-cover`}
                src={assetForUser(avatar)}
                alt={realName}
                title={realName}
                loading="lazy"
              />
              <Avatar.Fallback
                title={realName}
                className="flex size-full items-center justify-center"
              >
                {firstInitial ? (
                  <Typography
                    size={getTextSize()}
                    className="text-center uppercase"
                    weight="medium"
                  >
                    {firstInitial}
                    {lastInitial}
                  </Typography>
                ) : (
                  <Icon.User
                    style={{ width: size * 2.5, height: size * 2.5 }}
                  />
                )}
              </Avatar.Fallback>

              <span
                className={cx(
                  "absolute -bottom-0.5 -right-0.5 rounded-full bg-ochre-400 p-1 text-base-black",
                  {
                    hidden: !hasWorkedWith,
                  },
                )}
              >
                <Icon.Handshake />
              </span>
            </>
          )}
        </Avatar.Root>
      );

      if (onClick) {
        return (
          <button
            type="button"
            onClick={onClick}
            data-testid="avatar-button"
            aria-label={realName || "User avatar"}
          >
            {avatarComponent}
          </button>
        );
      }

      return avatarComponent;
    },
  ),
);

AvatarWithInitials.displayName = "AvatarWithInitials";
